import { authFetch } from "@/utils/api";

/**
 * Upload de mídia do CMS.
 *
 * Dois caminhos, escolhidos pelo tamanho:
 *
 * - **≤ 16 MB** → multipart para a própria API, que repassa ao S3. É o caminho
 *   antigo, mantido intacto: já funciona e não depende de CORS no bucket.
 * - **> 16 MB** → PUT direto ao S3 com URL pré-assinada. O arquivo não passa
 *   pela API, o que é obrigatório nessa faixa — o multer buffera o arquivo
 *   inteiro em memória, e o nginx da produção limita o corpo da requisição.
 *
 * O caminho grande usa XHR em vez de `fetch` por um motivo só: `fetch` não
 * reporta progresso de upload. Sem isso, 1 GB numa conexão comum fica vários
 * minutos sem nenhum sinal de vida na tela.
 */

export const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024; // 1 GB
export const PROXY_UPLOAD_MAX_BYTES = 16 * 1024 * 1024; // 16 MB

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  const decimals = exponent >= 2 && value < 10 ? 1 : 0;
  // `Number()` derruba o `.0`: o limite deve aparecer como "1 GB", não "1.0 GB".
  return `${Number(value.toFixed(decimals))} ${units[exponent]}`;
}

/** PUT no S3 com relato de progresso. Rejeita em erro de rede, abort e HTTP ≥400. */
function putWithProgress(uploadUrl, headers, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);

    for (const [name, value] of Object.entries(headers || {})) {
      xhr.setRequestHeader(name, value);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      // Um 403 aqui é quase sempre CORS ou assinatura, não permissão do usuário.
      reject(new Error(`Falha ao enviar para o S3 (HTTP ${xhr.status})`));
    };
    xhr.onerror = () =>
      reject(
        new Error(
          "Falha de rede no envio ao S3. Verifique a regra de CORS do bucket.",
        ),
      );
    xhr.onabort = () => reject(new Error("Upload cancelado"));

    xhr.send(file);
  });
}

async function uploadViaApi(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await authFetch("/api/internal/platform-content/admin/upload", {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Upload falhou (HTTP ${res.status})`);
  }
  return res.json();
}

async function uploadViaPresignedUrl(file, onProgress) {
  const res = await authFetch("/api/internal/platform-content/admin/upload-url", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      mimeType: file.type,
      size: file.size,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Falha ao preparar upload (HTTP ${res.status})`);
  }

  const { uploadUrl, headers, ...meta } = await res.json();
  await putWithProgress(uploadUrl, headers, file, onProgress);
  return meta;
}

/**
 * Envia um arquivo e devolve `{ url, filename, mimeType, size, key }`.
 *
 * @param {File} file
 * @param {{ onProgress?: (percent: number) => void }} [options]
 */
export async function uploadCmsFile(file, { onProgress } = {}) {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `Arquivo excede o limite de ${formatBytes(MAX_UPLOAD_BYTES)} (${formatBytes(file.size)})`,
    );
  }

  if (file.size <= PROXY_UPLOAD_MAX_BYTES) {
    return uploadViaApi(file);
  }

  return uploadViaPresignedUrl(file, onProgress);
}

export default uploadCmsFile;
